import { userType } from '@/constants/UserType';
import { isEmpty } from 'lodash';
import { defineStore } from 'pinia';
import type { User } from '../types';

export const useAuthStore = defineStore('auth', () => {
  const { data } = useAuth();
  const user = ref<User | null>((data.value?.data as User) || null);
  const getUser = computed(() => user.value || ({} as User));
  const hasLoggedInUser = computed(() => Boolean(user.value));
  const isVerified = computed(() => user.value?.email_verified);
  const hasCompletedOnboarding = computed(() => !isEmpty(user.value?.onboarded_at));
  const hasPersonalDetails = computed(
    () =>
      Boolean(
        user.value?.first_name &&
          user.value?.last_name &&
          user.value?.phone_number &&
          user.value?.address_place_id
      )
  );

  const accountType = computed(() => user.value?.account_type);
  const isCustomer = computed(() => user.value?.account_type === userType.CUSTOMER);
  const isMerchant = computed(() => user.value?.account_type === userType.MERCHANT);
  const isDriver = computed(() => user.value?.account_type === userType.DRIVER);
  const isPendingApproval = computed(() => isMerchant.value && Boolean(user.value?.requires_approval));

  const hasStripePayoutCompleted = computed(() => {
    const chargesEnabled = user.value?.charges_enabled ?? user.value?.stripe?.charges_enabled;
    const payoutsEnabled = user.value?.payouts_enabled ?? user.value?.stripe?.payouts_enabled;

    return Boolean(chargesEnabled && payoutsEnabled);
  });

  watch(
    () => data.value,
    () => {
      if (data.value?.data) {
        user.value = data.value?.data as User;
      }
    },
    { immediate: true }
  );

  return {
    user,
    getUser,
    hasLoggedInUser,
    hasCompletedOnboarding,
    hasStripePayoutCompleted,
    hasPersonalDetails,
    isVerified,
    accountType,
    isCustomer,
    isMerchant,
    isDriver,
    isPendingApproval,
  };
});
